import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { sendenQuiry } from "@/services/authService";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  address: "",
  Reason: "",
  message: "",
};
const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialFormState);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9+\-\s]{10,15}$/;
  const navigate = useNavigate();

  const validateForm = (form) => {
    if (!form.name.trim()) return "Full name is required";
    if (form.name.trim().length < 2) return "Full name is too short";

    if (!form.email.trim()) return "Email is required";
    if (!emailRegex.test(form.email)) return "Invalid email address";

    if (!form.phone.trim()) return "Phone number is required";
    if (!phoneRegex.test(form.phone)) return "Invalid phone number";

    if (!form.company.trim()) return "Company name is required";
    if (!form.address.trim()) return "Address is required";

    if (!form.Reason) return "Please select a reason for contacting us";

    if (!form.message.trim()) return "Message is required";
    if (form.message.trim().length < 10)
      return "Message must be at least 10 characters";

    return null; // ✅ valid
  };

  const submit = async (e) => {
    e.preventDefault();

    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return; // ❌ STOP API CALL
    }

    try {
      setLoading(true);
      const res = await sendenQuiry(form);
      toast.success(res?.message || "Enquiry submitted");
      setForm(initialFormState);
    } catch (err) {
      console.error(err);
      toast.error("Contact enquiry failed");
    } finally {
      setLoading(false);
    }
  };

  const RequiredLabel = ({ children }) => (
    <Label>
      {children} <span className="text-red-500">*</span>
    </Label>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1522071820081-009f0129c71c)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/70" />

        <div className="relative max-w-7xl mx-auto px-6 py-28 grid md:grid-cols-2 gap-16 text-white">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 text-white/90 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="space-y-8">
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
              Building the future of
              <br />
              <span className="font-bold">enterprise workforce systems</span>
            </h1>
            <p className="text-lg text-white/85 max-w-xl">
              We help organizations manage people, payroll, compliance, and
              performance through secure, scalable HRMS platforms designed for
              modern enterprises.
            </p>
            <div className="grid grid-cols-2 gap-6 max-w-md pt-4">
              <div className="bg-white/10 rounded-lg p-4 flex items-start gap-3">
                <Clock className="h-5 w-5 text-white/80 mt-0.5" />
                <div>
                  <p className="text-sm opacity-80">Response time</p>
                  <p className="font-medium">Within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="w-full h-[420px] rounded-xl overflow-hidden shadow-xl">
              <img
                src="/Contact.jpg"
                alt="Employees collaborating with laptops in office"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1504384308090-c894fdcc538d)",
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/80" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 text-white space-y-6">
            <h2 className="text-2xl font-semibold">
              Let’s talk workforce and payroll
            </h2>

            <p className="text-white/80 text-sm leading-relaxed">
              Share details about your workforce structure, payroll complexity,
              and compliance needs. Our team reviews every enquiry with a
              payroll-first, compliance-driven HRMS mindset.
            </p>

            <div className="space-y-4 text-sm text-white/70 pt-6">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-white/80 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p>D-601, Raikar Chambers</p>
                  <p>Govandi (East), Mumbai – 400 088</p>
                  <p>Maharashtra, India</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 text-sm text-white/70 pt-6">
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-white/80 shrink-0" />
                <p>+91-7738631003</p>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-white/80 shrink-0" />
                <p>support@elementree.co.in</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="border-none shadow-2xl backdrop-blur-xl bg-white/90 dark:bg-background/85">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-6">
                  Start the conversation
                </h3>

                <form
                  onSubmit={submit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <RequiredLabel>Full name</RequiredLabel>
                    <Input
                      value={form.name}
                      placeholder="Enter your full name"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, name: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <RequiredLabel>Work email</RequiredLabel>
                    <Input
                      type="email"
                      value={form.email}
                      placeholder="you@company.com"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, email: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <RequiredLabel>Phone (WhatsApp)</RequiredLabel>
                    <Input
                      value={form.phone}
                      placeholder="Enter your phone number"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, phone: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <RequiredLabel>Company / Organization</RequiredLabel>
                    <Input
                      value={form.company}
                      placeholder="Your company name"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, company: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <RequiredLabel>Address</RequiredLabel>
                    <Input
                      value={form.address}
                      placeholder="City, State, Country"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, address: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <RequiredLabel>Reason for contacting us</RequiredLabel>
                    <Select
                      value={form.Reason}
                      onValueChange={(value) =>
                        setForm((p) => ({ ...p, Reason: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Product enquiry">
                          Product enquiry
                        </SelectItem>
                        <SelectItem value="HRMS demo request">
                          HRMS demo request
                        </SelectItem>
                        <SelectItem value="General query">
                          General query
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <RequiredLabel>How Can We Help You?</RequiredLabel>
                    <Textarea
                      rows={4}
                      value={form.message}
                      placeholder="Briefly describe your requirement or question"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, message: e.target.value }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2 pt-2">
                    <Button disabled={loading} size="lg" className="w-full">
                      {loading ? "Submitting..." : "Submit enquiry"}
                    </Button>
                  </div>
                </form>

                <p className="text-xs text-muted-foreground mt-6">
                  We keep your contact details private and use them only to
                  follow up on your enquiry. Automated spam and repeated
                  submissions are filtered.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
